// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 42-app-marketplace.md
import * as React from 'react';
import { Eye, EyeOff, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { TagChips } from '@/components/apps-list/TagChips';
import { filterAppsByState, type TagMode } from '@/components/apps-list/filtering';
import { CardGrid } from '@/components/greenlight/CardGrid';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { RefreshWarning } from '@/components/greenlight/RefreshWarning';
import { MarketplaceAppCard } from '@/components/marketplace/MarketplaceAppCard';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { isTransientQueryError, liveQueryOptions } from '@/lib/polling';
import { useSessionCursorStack } from '@/lib/useSessionCursorStack';

// 21 = 7 full rows at the 3-column grid width — 25 leaves a trailing partial row.
const PAGE_SIZE = 21;

/**
 * Marketplace discovery grid (docs/42 §/marketplace): every role's browseable
 * view of the live apps in the org the viewer can open, one consumer card shape per row
 * (no restricted/lock variant yet — T-6173). Reuses the /apps search + tag
 * filter model and grid shell rather than forking AppsPage's management view.
 */
export function MarketplacePage(): React.JSX.Element {
  usePageTitle('Marketplace');

  const [searchParams, setSearchParams] = useSearchParams();
  const cursor = searchParams.get('cursor') ?? undefined;
  const q = searchParams.get('q') ?? '';
  const tags = (searchParams.get('tags') ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const tagMode: TagMode = searchParams.get('tagmode') === 'all' ? 'all' : 'any';
  // Show-hidden is carried in the URL like the other filters so a view is
  // shareable. Unlike /apps' old client-side toggle, the marketplace read is
  // paginated, so the flag goes to the server and pages stay full.
  const showHidden = searchParams.get('hidden') === '1';

  // Refresh-safe Previous trail; cleared whenever the view is back on page one.
  const trail = useSessionCursorStack('marketplace');
  const clearTrail = trail.clear;
  React.useEffect(() => {
    if (!cursor) clearTrail();
  }, [cursor, clearTrail]);

  const setParam = React.useCallback(
    (key: string, value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value == null || value === '') next.delete(key);
        else next.set(key, value);
        return next;
      });
    },
    [setSearchParams],
  );

  const queryKey = ['marketplace', cursor ?? null, showHidden] as const;
  const { data, isLoading, isError, error } = api.marketplace.list.useQuery(
    queryKey,
    {
      query: {
        limit: PAGE_SIZE,
        ...(showHidden ? { include_hidden: 'true' as const } : {}),
        ...(cursor ? { cursor } : {}),
      },
    },
    { ...liveQueryOptions, queryKey },
  );

  const rawItems = data?.body.items ?? [];
  const nextCursor = data?.body.next_cursor ?? null;
  const items = filterAppsByState(rawItems, { status: '', bucket: '', q, tags, tagMode });

  const toggleTag = (tag: string) => {
    const lower = tag.toLowerCase();
    const next = tags.some((t) => t.toLowerCase() === lower)
      ? tags.filter((t) => t.toLowerCase() !== lower)
      : [...tags, tag];
    setParam('tags', next.join(','));
  };

  function clearFilters(): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('q');
      next.delete('tags');
      next.delete('tagmode');
      return next;
    });
  }

  // Changing the flag changes the result set, so the cursors collected walking
  // forward no longer address the same pages. Dropping `cursor` returns to page
  // one, which is what clears the trail.
  function toggleShowHidden(): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('cursor');
      if (showHidden) next.delete('hidden');
      else next.set('hidden', '1');
      return next;
    });
  }

  function goNext(): void {
    if (!nextCursor) return;
    trail.push(cursor ?? '');
    setParam('cursor', nextCursor);
  }

  function goPrev(): void {
    const prev = trail.pop();
    // No trail (a shared link in a fresh tab): fall back to the first page
    // rather than dead-ending on the cursor page.
    setParam('cursor', prev === undefined || prev === '' ? null : prev);
  }

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      {isError && data?.status === 200 && isTransientQueryError(error) && <RefreshWarning />}
      <div>
        <Eyebrow>Discover</Eyebrow>
        <h1 className="text-2xl font-semibold tracking-[-0.02em] md:text-[28px]">Marketplace</h1>
      </div>

      <div className="space-y-3">
        <div className="relative max-w-[320px]">
          <Search
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            placeholder="Search name or slug"
            aria-label="Search by name or slug"
            value={q}
            onChange={(e) => setParam('q', e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TagChips
            items={rawItems}
            activeTags={tags}
            tagMode={tagMode}
            onToggleTag={toggleTag}
            onTagModeChange={(m) => setParam('tagmode', m === 'all' ? 'all' : null)}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-11 shrink-0 gap-1.5 text-muted-foreground md:h-8"
            aria-pressed={showHidden}
            data-testid="marketplace-show-hidden"
            onClick={toggleShowHidden}
          >
            {showHidden ? (
              <EyeOff className="size-4" aria-hidden />
            ) : (
              <Eye className="size-4" aria-hidden />
            )}
            {showHidden ? 'Hide hidden apps' : 'Show hidden apps'}
          </Button>
        </div>
      </div>

      {isError && (data?.status !== 200 || !isTransientQueryError(error)) ? (
        <Alert variant="destructive" data-testid="marketplace-error">
          <AlertTitle>Couldn't load the marketplace</AlertTitle>
          <AlertDescription>
            Something went wrong fetching the catalog. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div data-testid="marketplace-loading">
          <CardGrid columns={3}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[260px] w-full" />
            ))}
          </CardGrid>
        </div>
      ) : rawItems.length === 0 ? (
        <div
          className="rounded-gl border border-border p-8 text-center"
          data-testid="marketplace-empty"
        >
          <p className="text-sm font-medium text-foreground">No apps in the marketplace yet.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Apps show up here once a colleague deploys one and it's live.
          </p>
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-gl border border-border p-8 text-center"
          data-testid="marketplace-no-match"
        >
          <p className="text-sm text-muted-foreground">No apps match the current filters.</p>
          <Button variant="ghost" size="sm" className="mt-2" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          <div data-testid="marketplace-grid">
            <CardGrid columns={3}>
              {items.map((app) => (
                <MarketplaceAppCard key={app.app_id} app={app} />
              ))}
            </CardGrid>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={!cursor}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={!nextCursor}
              data-testid="marketplace-next"
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
